import { useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { ArrowLeft } from "@phosphor-icons/react"
import { LoadingSpinner } from "@/components/loading-spinner"
import { Banner } from "@/components/ui/banner"
import { Button } from "@/components/ui/button"
import { BetterDocSessionDetailContent } from "@/modules/admin/better-doc/components/better-doc-session-detail-content"
import { useBetterDocFetch } from "@/modules/admin/better-doc/hooks/useBetterDocFetch"
import { fetchBetterDocRemoteRecommendation } from "@/modules/admin/better-doc/services/betterDocRemoteRecommendationQuery"
import { fetchBetterDocSession } from "@/modules/admin/better-doc/services/betterDocSessionQuery"
import { BetterDocLiveRecommendationState } from "@/modules/admin/better-doc/types/BetterDocLiveRecommendationState"
import { BetterDocSessionActionOutcome } from "@/modules/admin/better-doc/types/BetterDocSessionActionOutcome"

type BetterDocSessionDetailPanelProps = {
  sessionId: number
  onClose: () => void
}

type BetterDocSessionDetailRequest = {
  sessionId: number
  reloadCount: number
}

const fetchBetterDocSessionForRequest = ({ sessionId }: BetterDocSessionDetailRequest) =>
  fetchBetterDocSession(sessionId)

export const BetterDocSessionDetailPanel = ({ sessionId, onClose }: BetterDocSessionDetailPanelProps) => {
  const { t } = useTranslation()
  const [reloadCount, setReloadCount] = useState(0)
  const [actionOutcome, setActionOutcome] = useState<BetterDocSessionActionOutcome | null>(null)
  const [liveRecommendationState, setLiveRecommendationState] = useState<BetterDocLiveRecommendationState>({
    status: "idle",
  })
  const latestLiveRecommendationLoad = useRef(0)
  const sessionRequest = useMemo(() => ({ sessionId, reloadCount }), [sessionId, reloadCount])
  const sessionState = useBetterDocFetch(sessionRequest, fetchBetterDocSessionForRequest)

  const reloadSession = () => setReloadCount((currentReloadCount) => currentReloadCount + 1)

  const handleActionCompleted = (outcome: BetterDocSessionActionOutcome) => {
    setActionOutcome(outcome)
    reloadSession()
  }

  const handleLiveRecommendationLoad = (recommendationId: string) => {
    const liveRecommendationLoad = latestLiveRecommendationLoad.current + 1
    latestLiveRecommendationLoad.current = liveRecommendationLoad
    setLiveRecommendationState((currentState) => ({
      status: "loading",
      previousData: currentState.status === "loaded" ? currentState.data : null,
    }))

    fetchBetterDocRemoteRecommendation(recommendationId)
      .then((data) => {
        if (latestLiveRecommendationLoad.current === liveRecommendationLoad) {
          setLiveRecommendationState({ status: "loaded", data })
          reloadSession()
        }
      })
      .catch(() => {
        if (latestLiveRecommendationLoad.current === liveRecommendationLoad) {
          setLiveRecommendationState({ status: "failed" })
          reloadSession()
        }
      })
  }

  const visibleSessionState =
    sessionState.status === "loading" && sessionState.previousState !== null ? sessionState.previousState : sessionState

  const renderContent = () => {
    switch (visibleSessionState.status) {
      case "idle":
      case "loading":
        return <LoadingSpinner size={24} className="flex justify-center p-8" />
      case "failed":
        return (
          <Banner type="inline" variant="critical" data-testid="better-doc-admin-session-error">
            {t("admin.betterDoc.requestFailed")}
          </Banner>
        )
      case "loaded":
        return visibleSessionState.data === null ? (
          <Banner type="inline" variant="info" data-testid="better-doc-admin-session-not-found">
            {t("admin.betterDoc.sessionDetail.notFound", { sessionId })}
          </Banner>
        ) : (
          <BetterDocSessionDetailContent
            detail={visibleSessionState.data}
            actionOutcome={actionOutcome}
            onActionCompleted={handleActionCompleted}
            liveRecommendationState={liveRecommendationState}
            onLiveRecommendationLoad={handleLiveRecommendationLoad}
          />
        )
    }
  }

  return (
    <div className="flex flex-col gap-4" data-testid="better-doc-admin-session-detail">
      <div>
        <Button type="button" variant="tertiary" onClick={onClose} data-testid="better-doc-admin-session-back-button">
          <ArrowLeft className="size-4" />
          {t("admin.betterDoc.sessionDetail.back")}
        </Button>
      </div>
      {renderContent()}
    </div>
  )
}
